import React from "react";

import { supabase } from "@/services/supabaseClient";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

function resolvedName(...candidates) {
  const name = resolvePatientFacingDoctorName(...candidates);
  return name === "Doctor" ? "" : name;
}

/**
 * Resolves the public clinician label for patient presentation without using
 * booking authority or exposing an internal clinician identifier.
 */
export function usePatientFacingClinicianName({ doctor, doctorId, doctorName } = {}) {
  const immediateName = React.useMemo(
    () => resolvedName(doctor, doctorName),
    [doctor, doctorName],
  );
  const [directoryName, setDirectoryName] = React.useState("");
  const [directoryLookupSettled, setDirectoryLookupSettled] = React.useState(false);

  React.useEffect(() => {
    const clinicianId = String(doctorId || "").trim();
    if (immediateName || !clinicianId) {
      setDirectoryName("");
      setDirectoryLookupSettled(true);
      return undefined;
    }

    let cancelled = false;
    setDirectoryName("");
    setDirectoryLookupSettled(false);

    void supabase
      .from("v_doctor_directory_browse")
      .select("display_name")
      .eq("doctor_user_id", clinicianId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        setDirectoryName(error ? "" : resolvedName(data?.display_name));
        setDirectoryLookupSettled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [doctorId, immediateName]);

  return {
    name: immediateName || directoryName,
    isLoading: Boolean(!immediateName && !directoryName && doctorId && !directoryLookupSettled),
  };
}
